import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Paper,
  CircularProgress,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import InputIcon from "@material-ui/icons/Input";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import { PDFViewer } from "../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
import CommonDigitizedDocument from "../Digitisation/CommonDigitizedDocument";
import DigitizedErrorBoundary from "./DigitizedErrorBoundary";
import DocxPreview from "./DocxPreview";
import Pagination from "../logisticsDashboard/components/Pagination";
import {
  fetchDocumentRepositoryFilters,
  fetchDocumentRepositoryBatches,
  fetchDocumentRepositoryCMOs,
  fetchDocumentRepositoryPOs,
  fetchDocumentRepository,
  saveDocumentRepositoryFilters,
  downloadCustomFileById,
  downloadStaticFile,
  fetchBatchDetailsForReview,
} from "./documentRepositoryAPI";

const MENU_PROPS = {
  PaperProps: { style: { maxHeight: 320 } },
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
};

// "Financial" is a client-side pseudo-category. Selecting it queries the backend
// with the "Pre-Shipment Documents" category, then narrows the returned rows to
// Commercial Invoices only.
const FINANCIAL_CATEGORY = "Financial";
const FINANCIAL_BACKEND_CATEGORY = "Pre-Shipment Documents";
const FINANCIAL_DOCUMENT_TYPE = "Commercial Invoice";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 3, 3, 3),
    width: "100%",
    boxSizing: "border-box",
    minHeight: "calc(100vh - 120px)",
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.4rem",
    color: "#111",
    marginBottom: theme.spacing(2),
  },
  filterLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: "#8a8f99",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  select: {
    minWidth: 220,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-input": { padding: "10px 14px", fontSize: "0.9rem" },
  },
  saveBtn: {
    backgroundColor: "#EB5E28",
    color: "#fff",
    textTransform: "none",
    borderRadius: "2rem",
    height: 42,
    padding: "6px 22px",
    fontSize: "0.9rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#000", color: "#fff" },
    "&.Mui-disabled": { backgroundColor: "#EB5E28", color: "#fff", opacity: 0.6 },
  },
  clearLink: {
    color: "#1976d2",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    textDecoration: "underline",
    whiteSpace: "nowrap",
    "&:hover": { color: "#0d47a1" },
  },
  searchField: {
    width: "100%",
    maxWidth: 420,
    "& .MuiOutlinedInput-root": { borderRadius: 8, backgroundColor: "#fff" },
  },
  downloadBtn: {
    textTransform: "none",
    borderRadius: 8,
    borderColor: "#c9ccd3",
    color: "#333",
    fontWeight: 500,
    height: 40,
    padding: "6px 18px",
    whiteSpace: "nowrap",
    "&:hover": { borderColor: "#333", backgroundColor: "#f7f7f7" },
  },
  tableWrap: {
    marginTop: 16,
    border: "1px solid #e8e8ec",
    borderRadius: 10,
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: "calc(100vh - 340px)",
  },
  headerRow: {
    backgroundColor: "#f6f7f9",
    "& th": {
      backgroundColor: "#f6f7f9",
      fontWeight: 700,
      fontSize: "0.7rem",
      letterSpacing: "0.04em",
      color: "#8a8f99",
      textTransform: "uppercase",
      padding: "12px 12px",
      borderBottom: "1px solid #e8e8ec",
      whiteSpace: "nowrap",
    },
  },
  bodyRow: {
    "&:hover": { backgroundColor: "#fafbfc" },
    "& td": {
      fontSize: "0.9rem",
      padding: "12px 12px",
      borderBottom: "1px solid #f0f0f2",
      color: "#333",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
    },
  },
  // The two free-text columns are the widest content; let them wrap (capped)
  // instead of forcing the whole table past the viewport edge. A <td> maxWidth
  // is unreliable in auto-layout tables, so the width is enforced on an inner
  // block element with overflow-wrap to break long tokens too.
  descCell: { whiteSpace: "normal" },
  docNameCell: { whiteSpace: "normal" },
  clampDesc: {
    maxWidth: 220,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  clampDocName: {
    maxWidth: 260,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  pdfBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FCE9E4",
    color: "#D2673E",
    fontSize: "0.6rem",
    fontWeight: 700,
    borderRadius: 4,
    padding: "3px 6px",
    marginRight: 10,
    letterSpacing: "0.03em",
    flexShrink: 0,
  },
  docName: { fontWeight: 600, color: "#222" },
  docId: {
    color: "#3a6ea5",
    fontSize: "0.82rem",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  categoryChip: {
    display: "inline-block",
    backgroundColor: "#eef1f6",
    color: "#4a5568",
    fontSize: "0.75rem",
    fontWeight: 600,
    borderRadius: "1rem",
    padding: "3px 12px",
  },
  reviewBtn: {
    textTransform: "none",
    borderRadius: "2rem",
    backgroundColor: "#FCEEE9",
    border: "1px solid #F3C9B8",
    color: "#C15B3C",
    fontWeight: 600,
    fontSize: "0.76rem",
    padding: "5px 14px",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#F9E0D6" },
  },
  iconAction: { color: "#6b7280", padding: 6 },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
    paddingTop: 4,
  },
  footerNote: { fontSize: "0.76rem", color: "#9aa0ad", fontStyle: "italic" },
  selectedText: { fontSize: "0.85rem", color: "#444" },
}));

const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

// Row identity — prefer an explicit rowId (static docs, unique per file), then
// the vault file id (unique + used by downloadCustomFile), then the human-facing
// document id. Testing reports can share an Analytical Report id, so documentId
// alone is not unique for them.
const rowKey = (doc) =>
  doc.rowId != null
    ? doc.rowId
    : doc.schrockenCustomFileID != null
    ? doc.schrockenCustomFileID
    : doc.documentId;

// The backend returns uploadDate as an ISO timestamp (2026-03-12T…); render it
// as "12 Mar 2026". Non-ISO values (e.g. already-formatted demo strings) are
// passed through unchanged.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatUploadDate = (value) => {
  if (!value) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value; // not a parseable date — leave as-is
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// Display-only rename: this document type has a long official name; the table
// shows the shortened label while the underlying value is unchanged.
const DOCUMENT_TYPE_DISPLAY = {
  "Confirmation of the Partial Manufacturing of a Medicinal Product":
    "Confirmation of the Partial Manufacturing",
};
const displayDocumentType = (documentType) =>
  DOCUMENT_TYPE_DISPLAY[documentType] || documentType;

// Resolve the delivery-schedule batch id for a document. Some document types
// (e.g. Packing List, Commercial Invoice) come back with a null
// schrockenDeliveryScheduleBatchID even though a sibling document on the SAME
// batch does carry one. Fall back to that sibling's id so "Review Digitalized
// Fields" can fetch the real batch-details payload for every row of a batch.
const resolveScheduleBatchId = (doc, documents) => {
  if (!doc) return null;
  if (doc.schrockenDeliveryScheduleBatchID != null)
    return doc.schrockenDeliveryScheduleBatchID;
  const batchNo = doc.cmoBatchNumber || doc.batchNumber;
  if (!batchNo) return null;
  const sibling = (documents || []).find(
    (d) =>
      d.schrockenDeliveryScheduleBatchID != null &&
      (d.cmoBatchNumber || d.batchNumber) === batchNo
  );
  return sibling ? sibling.schrockenDeliveryScheduleBatchID : null;
};

// Build the batchDetails shape CommonDigitizedDocument expects, from the
// selected table row's digitized data (data taken from table row data). The
// doc's schrockenDeliveryScheduleBatchID is already resolved (see handleReview),
// falling back to a same-batch sibling's id where the row itself has none.
const buildBatchDetails = (doc) => {
  const kvp = (doc && doc.digitizedData && doc.digitizedData.kvpJson) || {};
  const fileId = doc && doc.schrockenCustomFileID;
  return {
    digitizedExtractedData: [
      {
        schrockenCustomFileID: fileId,
        documentType: doc && doc.documentType,
        kvpJsonOriginal: kvp,
        kvpJsonEdited: kvp,
      },
    ],
    customFilesList: [
      {
        schrockenCustomFileID: fileId != null ? String(fileId) : "",
        documentCategoryType: doc && doc.documentCategory,
      },
    ],
    confidenceLevels: { lowConfidenceLimit: 80, highConfidenceLimit: 90 },
    partialShippingDetails: [],
    schrockenDeliveryScheduleBatchID: doc && doc.schrockenDeliveryScheduleBatchID,
  };
};

const DocumentRepository = () => {
  const classes = useStyles();

  const [filters, setFilters] = useState({
    products: [],
    batches: [],
    documentTypes: [],
    pos: [],
    cmos: [],
  });
  const [cmo, setCmo] = useState("all");
  const [product, setProduct] = useState("all");
  const [po, setPo] = useState("all");
  const [batch, setBatch] = useState("all");
  const [docType, setDocType] = useState("all"); // Document Category
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // The CMO filter is a sponsor-side control (a CMO user is a single org with
  // nothing to choose), mirroring the Purchase Order Collaboration CMO filter,
  // whose fetchCMOMembers endpoint lives only on the sponsor router. So it is
  // shown/loaded only when the logged-in user is NOT a CMO.
  const isCmoUser = (localStorage.getItem("collabType") || "") === "CMO";

  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [reviewDoc, setReviewDoc] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewBatchDetails, setReviewBatchDetails] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const digitizedCloseRef = useRef(null);

  // Open "Review Digitalized Fields": fetch the real batch-details payload
  // (same source Batch Details uses) so CommonDigitizedDocument gets the exact
  // shape it expects; fall back to the row's own digitized data otherwise.
  const handleReview = async (doc) => {
    // The clicked row may not carry its own schrockenDeliveryScheduleBatchID
    // (e.g. Packing List / Commercial Invoice); borrow it from a sibling
    // document on the same batch when available.
    const scheduleBatchId = resolveScheduleBatchId(doc, documents);
    setReviewDoc({ ...doc, schrockenDeliveryScheduleBatchID: scheduleBatchId });
    setReviewBatchDetails(null);
    if (scheduleBatchId != null) {
      setReviewLoading(true);
      const res = await fetchBatchDetailsForReview({
        schrockenDeliveryScheduleBatchID: scheduleBatchId,
        batchNumber: doc.cmoBatchNumber || doc.batchNumber,
        poNumber: doc.poNumber,
      });
      setReviewLoading(false);
      if (res && res.status && res.data) setReviewBatchDetails(res.data);
    }
  };

  const closeReview = () => {
    setReviewDoc(null);
    setReviewBatchDetails(null);
    setReviewLoading(false);
  };

  // Load filter option lists once (from the middleware).
  useEffect(() => {
    let active = true;
    fetchDocumentRepositoryFilters().then((res) => {
      if (active && res && res.status) {
        setFilters((prev) => ({
          ...prev,
          products: res.products || [],
          batches: res.batches || [],
          documentTypes: res.documentTypes || [],
          pos: res.pos || [],
        }));
      }
    });
    // CMO options come from the same fetchCMOMembers endpoint the Purchase
    // Order Collaboration filter uses; only sponsor-side (non-CMO) users pick a
    // CMO, so skip the call for CMO users (it would 404 on their router).
    if (!isCmoUser) {
      fetchDocumentRepositoryCMOs().then((res) => {
        if (active && res && res.status) {
          setFilters((prev) => ({ ...prev, cmos: res.cmos || [] }));
        }
      });
    }
    return () => {
      active = false;
    };
  }, [isCmoUser]);

  // Load the document listing whenever filters or applied search change.
  const loadDocuments = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    // "Financial" maps to the "Pre-Shipment Documents" backend category, then
    // the returned rows are narrowed to Commercial Invoices client-side.
    const isFinancial = docType === FINANCIAL_CATEGORY;
    fetchDocumentRepository({
      cmoId: cmo,
      productId: product,
      poNumber: po,
      batchNumber: batch,
      documentType: isFinancial ? FINANCIAL_BACKEND_CATEGORY : docType,
      searchText: appliedSearch,
    })
      .then((res) => {
        // On API failure the data layer returns status:false with an empty
        // list — surface an error state rather than any placeholder/demo rows.
        setLoadError(!(res && res.status));
        let list = (res && res.documentList) || [];
        if (isFinancial) {
          list = list.filter((d) => d.documentType === FINANCIAL_DOCUMENT_TYPE);
        }
        // Commercial Invoices are surfaced under the "Financial" category in the
        // table, even though the backend returns them as "Pre-Shipment Documents".
        list = list.map((d) =>
          d.documentType === FINANCIAL_DOCUMENT_TYPE
            ? { ...d, documentCategory: FINANCIAL_CATEGORY }
            : d
        );
        setDocuments(list);
        setSelected(new Set());
      })
      .catch(() => {
        setLoadError(true);
        setDocuments([]);
        setSelected(new Set());
      })
      .finally(() => setLoading(false));
  }, [cmo, product, po, batch, docType, appliedSearch]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // The batch dropdown is populated server-side and cascades on the selected
  // product (see handleProductChange), so filters.batches already holds the
  // batches valid for the current product — no client-side filtering needed.
  const batchOptions = filters.batches;

  // Cascade: when the CMO changes, reset the dependent PO filter and re-fetch
  // the PO options scoped to that CMO (POST .../POs { cmoId }). The product →
  // batch cascade below is independent and unaffected.
  const handleCmoChange = (e) => {
    const nextCmo = e.target.value;
    setCmo(nextCmo);
    setPo("all"); // reset dependent PO filter
    fetchDocumentRepositoryPOs(nextCmo === "all" ? undefined : nextCmo).then((res) => {
      if (res && res.status) {
        setFilters((prev) => ({ ...prev, pos: res.pos || [] }));
      }
    });
  };

  // Cascade: when the product changes, reset the dependent batch filter and
  // re-fetch the batch options scoped to that product (POST .../Batches { productId }).
  const handleProductChange = (e) => {
    const nextProduct = e.target.value;
    setProduct(nextProduct);
    setBatch("all"); // reset dependent batch filter
    fetchDocumentRepositoryBatches(nextProduct === "all" ? undefined : nextProduct).then(
      (res) => {
        if (res && res.status) {
          setFilters((prev) => ({ ...prev, batches: res.batches || [] }));
        }
      }
    );
  };

  const handleSearch = () => setAppliedSearch(searchInput);

  const handleClearFilters = () => {
    setCmo("all");
    setProduct("all");
    setPo("all");
    setBatch("all");
    setDocType("all");
    setSearchInput("");
    setAppliedSearch("");
    // Product is back to "all" — restore the full (unscoped) batch list.
    fetchDocumentRepositoryBatches().then((res) => {
      if (res && res.status) {
        setFilters((prev) => ({ ...prev, batches: res.batches || [] }));
      }
    });
    // CMO is back to "all" — restore the full (unscoped) PO list.
    fetchDocumentRepositoryPOs().then((res) => {
      if (res && res.status) {
        setFilters((prev) => ({ ...prev, pos: res.pos || [] }));
      }
    });
  };

  const handleSaveFilters = async () => {
    setSaving(true);
    const res = await saveDocumentRepositoryFilters({ cmo, product, po, batch, docType });
    setSaving(false);
    setSnack({
      open: true,
      msg: res && res.status ? "Filters saved successfully!" : "Failed to save filters.",
      severity: res && res.status ? "success" : "error",
    });
  };

  // Reset to the first page whenever the result set or page size changes.
  useEffect(() => {
    setPage(1);
  }, [documents, pageSize]);

  const totalPages = Math.max(1, Math.ceil(documents.length / pageSize));
  const safePage = Math.min(page, totalPages);
  // Pagination disabled — show ALL documents (sticky header + scrollable area).
  const pageDocs = documents;

  const allSelected = documents.length > 0 && selected.size === documents.length;
  const someSelected = selected.size > 0 && selected.size < documents.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(documents.map((d) => rowKey(d))));
    }
  };

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Download one or more documents via /downloadCustomFile.
  const handleDownload = async (docs) => {
    const list =
      docs && docs.length ? docs : documents.filter((d) => selected.has(rowKey(d)));
    if (!list.length) return;
    setDownloading(true);
    setSnack({ open: true, msg: "Preparing download...", severity: "info" });
    let ok = 0;
    for (const doc of list) {
      // Static Quality rows are served as assets (public/qualityDocuments) and
      // downloaded by URL; API rows continue to stream via /downloadCustomFile.
      // eslint-disable-next-line no-await-in-loop
      const res =
        doc.isDemo && doc.downloadUrl
          ? await downloadStaticFile(doc.downloadUrl, `${doc.documentName}.docx`)
          : await downloadCustomFileById(doc.schrockenCustomFileID, doc.documentId);
      if (res && res.status) ok += 1;
    }
    setDownloading(false);
    if (ok > 0) {
      setSnack({
        open: true,
        msg: `${ok} document${ok > 1 ? "s" : ""} downloaded.`,
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        msg: "Document file is not available to download in this environment.",
        severity: "warning",
      });
    }
  };

  return (
    <Box className={classes.root}>
      {/* <Typography className={classes.pageTitle}>Document Repository</Typography> */}

      {/* ── Filters row ── */}
      <Grid container alignItems="flex-end" justify="space-between" style={{ rowGap: 12 }}>
        <Grid item style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          {/* CMO filter — sponsor-side only (same fetchCMOMembers source as the
              Purchase Order Collaboration CMO filter). Hidden for CMO users. */}
          {!isCmoUser && (
            <Box>
              <Typography className={classes.filterLabel}>CMO</Typography>
              <FormControl variant="outlined" size="small" className={classes.select} style={{ minWidth: 200 }}>
                <Select value={cmo} onChange={handleCmoChange} MenuProps={MENU_PROPS}>
                  <MenuItem value="all">All CMOs</MenuItem>
                  {filters.cmos.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box>
            <Typography className={classes.filterLabel}>Product</Typography>
            <FormControl variant="outlined" size="small" className={classes.select}>
              <Select value={product} onChange={handleProductChange} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All products</MenuItem>
                {filters.products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography className={classes.filterLabel}>PO</Typography>
            <FormControl variant="outlined" size="small" className={classes.select} style={{ minWidth: 160 }}>
              <Select value={po} onChange={(e) => setPo(e.target.value)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All POs</MenuItem>
                {filters.pos.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography className={classes.filterLabel}>Batch</Typography>
            <FormControl variant="outlined" size="small" className={classes.select} style={{ minWidth: 160 }}>
              <Select value={batch} onChange={(e) => setBatch(e.target.value)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All batches</MenuItem>
                {batchOptions.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography className={classes.filterLabel}>Document Category</Typography>
            <FormControl variant="outlined" size="small" className={classes.select}>
              <Select value={docType} onChange={(e) => setDocType(e.target.value)} MenuProps={MENU_PROPS}>
                <MenuItem value="all">All categories</MenuItem>
                <MenuItem value={FINANCIAL_CATEGORY}>{FINANCIAL_CATEGORY}</MenuItem>
                {filters.documentTypes.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Button className={classes.saveBtn} onClick={handleSaveFilters} disabled={saving}>
            {saving ? (
              <>
                <CircularProgress size={15} style={{ color: "#fff", marginRight: 8 }} />
                Saving...
              </>
            ) : (
              "Save filters"
            )}
          </Button>
          <Typography className={classes.clearLink} onClick={handleClearFilters}>
            Clear filters
          </Typography>
        </Grid>

        <Grid item style={{ display: "flex", alignItems: "center", gap: 12 }}>
          
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={loadDocuments} disabled={loading}>
                <Typography className={classes.clearLink} onClick={handleClearFilters}>
                  Refresh
                </Typography>
                <RefreshIcon fontSize="small" style={{ color: "#1976d2" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* ── Search + download row ── */}
      <Grid container alignItems="center" justify="space-between" style={{ marginTop: 18, rowGap: 12 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Search documents, batches, steps…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#9aa0ad" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput("");
                      setAppliedSearch("");
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
        <Grid item style={{ display: "flex", alignItems: "center", gap: 16 }}>
          
          <Typography className={classes.selectedText}>{selected.size} selected</Typography>
          <Button
            variant="outlined"
            className={classes.downloadBtn}
            startIcon={downloading ? <CircularProgress size={16} /> : <GetAppIcon />}
            disabled={selected.size === 0 || downloading}
            onClick={() => handleDownload()}
          >
            Download
          </Button>
          {/* PER PAGE selector commented out — the table shows all documents
              with a sticky header inside a scrollable area. */}
          {/* <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography style={{ fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.03em", color: "#333", whiteSpace: "nowrap" }}>
              PER PAGE
            </Typography>
            <FormControl variant="outlined" size="small">
              <Select
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                style={{ backgroundColor: "#fff" }}
                MenuProps={MENU_PROPS}
              >
                {[5, 10, 15, 20, 25, 30].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}
        </Grid>
      </Grid>

      {/* ── Table ── */}
      <Paper elevation={0} className={classes.tableWrap}>
        <Table stickyHeader>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell padding="checkbox" style={{ paddingLeft: 16 }}>
                <Checkbox
                  color="primary"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Product Description</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Document ID</TableCell>
              <TableCell>Document Category</TableCell>
              <TableCell>Batch Number</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} style={{ textAlign: "center", padding: 48 }}>
                  <CircularProgress size={28} thickness={4} />
                  <Typography style={{ marginTop: 12, color: "#888", fontSize: "0.85rem" }}>
                    Loading documents...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell colSpan={8} style={{ textAlign: "center", padding: 48, color: "#c0392b" }}>
                  Unable to load documents. Please try again later.
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} style={{ textAlign: "center", padding: 48, color: "#888" }}>
                  No documents found for the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              pageDocs.map((doc) => {
                const key = rowKey(doc);
                return (
                  <TableRow key={key} className={classes.bodyRow}>
                    <TableCell padding="checkbox" style={{ paddingLeft: 16 }}>
                      <Checkbox
                        color="primary"
                        checked={selected.has(key)}
                        onChange={() => toggleRow(key)}
                      />
                    </TableCell>
                    <TableCell className={classes.descCell}>
                      <Box className={classes.clampDesc}>{doc.productDescription}</Box>
                    </TableCell>
                    <TableCell className={classes.docNameCell}>
                      <Box className={classes.clampDocName} style={{ display: "flex", alignItems: "flex-start" }}>
                        <span className={classes.pdfBadge} style={{ marginTop: 2 }}>{doc.fileFormat || "PDF"}</span>
                        <span className={classes.docName}>{displayDocumentType(doc.documentType)}</span>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <span className={classes.docId} onClick={() => setPreviewDoc(doc)}>
                        {doc.documentId}
                      </span>
                    </TableCell>
                    <TableCell>
                      {doc.documentCategory ? (
                        <span className={classes.categoryChip}>{doc.documentCategory}</span>
                      ) : (
                        <span style={{ color: "#b0b4bd" }}>—</span>
                      )}
                    </TableCell>
                    <TableCell>{doc.batchNumber || <span style={{ color: "#b0b4bd" }}>—</span>}</TableCell>
                    <TableCell>{formatUploadDate(doc.uploadDate) || <span style={{ color: "#b0b4bd" }}>—</span>}</TableCell>
                    <TableCell align="right">
                      <Box style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                        <Button
                          className={classes.reviewBtn}
                          startIcon={<InputIcon style={{ fontSize: 16 }} />}
                          onClick={() => handleReview(doc)}
                        >
                          Review Digitalized Fields
                        </Button>
                        <Tooltip title="View document">
                          <IconButton className={classes.iconAction} size="small" onClick={() => setPreviewDoc(doc)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            className={classes.iconAction}
                            size="small"
                            onClick={() => handleDownload([doc])}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination commented out — all documents render in the scrollable table above. */}
      {false && !loading && !loadError && documents.length > pageSize && (
        <Box style={{ marginTop: 8 }}>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </Box>
      )}

      {/* ── Footer ── */}
      {/* <Box className={classes.footer}>
        <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Typography className={classes.selectedText}>
            {selected.size} of {documents.length} selected
          </Typography>
          {selected.size > 0 && (
            <Typography className={classes.clearLink} onClick={() => setSelected(new Set())}>
              Clear selection
            </Typography>
          )}
        </Box>
        <Typography className={classes.footerNote}>
          All documents are CDMO GMP source records · audit-trailed
        </Typography>
      </Box> */}

      {/* Review Digitalized Fields — opens the shared CommonDigitizedDocument
          viewer. Uses the real batch-details payload (fetchDeliveryScheduleBatchDetails)
          when available, else a lightweight view built from the row data. */}
      <Dialog open={!!reviewDoc} onClose={closeReview} maxWidth="lg" fullWidth fullScreen>
        <DialogTitle style={{ paddingBottom: 4 }}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography style={{ fontWeight: 700, fontSize: "1.05rem" }}>
              Review Digitalized Fields
              {reviewDoc ? ` · ${reviewDoc.documentName} · ${reviewDoc.documentId}` : ""}
            </Typography>
            <IconButton size="small" onClick={closeReview}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {reviewDoc && reviewLoading ? (
            <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60 }}>
              <CircularProgress size={30} />
              <Typography style={{ marginTop: 14, color: "#888", fontSize: "0.9rem" }}>
                Loading digitized fields...
              </Typography>
            </Box>
          ) : reviewDoc ? (
            <DigitizedErrorBoundary onClose={closeReview}>
              <CommonDigitizedDocument
                initialData={(reviewDoc.digitizedData && reviewDoc.digitizedData.kvpJson) || {}}
                onClose={closeReview}
                handleCloseRef={digitizedCloseRef}
                schrockenCustomFileID={reviewDoc.schrockenCustomFileID}
                digitizedData={{
                  documentType: reviewDoc.documentType,
                  schrockenCustomFileID: reviewDoc.schrockenCustomFileID,
                  documentCategoryType: reviewDoc.documentCategory,
                  kvpJson: (reviewDoc.digitizedData && reviewDoc.digitizedData.kvpJson) || {},
                }}
                batchDetails={reviewBatchDetails || buildBatchDetails(reviewDoc)}
                poNumber={reviewDoc.poNumber || reviewDoc.batchNumber}
                batchNumber={reviewDoc.batchNumber}
                cmoBatchNumber={reviewDoc.cmoBatchNumber || reviewDoc.batchNumber}
                previewFileUrl={reviewDoc.viewUrl}
              />
            </DigitizedErrorBoundary>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Document preview — renders the real PDF/Excel via /downloadCustomFile. */}
      <Dialog open={!!previewDoc} onClose={() => setPreviewDoc(null)} maxWidth="lg" fullWidth>
        <DialogTitle style={{ paddingBottom: 4 }}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography style={{ fontWeight: 700, fontSize: "1.05rem" }}>
              {previewDoc ? `${previewDoc.documentName} · ${previewDoc.documentId}` : ""}
            </Typography>
            <Box>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={() => previewDoc && handleDownload([previewDoc])}
                  style={{ marginRight: 4 }}
                >
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setPreviewDoc(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewDoc && previewDoc.isDemo && previewDoc.viewUrl ? (
            // Static Quality rows are bundled .docx assets: render them inline
            // with docx-preview (the same file is offered via Download).
            <DocxPreview url={previewDoc.viewUrl} />
          ) : previewDoc && previewDoc.schrockenCustomFileID ? (
            <PDFViewer schrockenCustomFileID={previewDoc.schrockenCustomFileID} />
          ) : previewDoc ? (
            <Box style={{ padding: 48, textAlign: "center", color: "#6b7280" }}>
              <VisibilityOutlinedIcon style={{ fontSize: 40, color: "#c9ccd3" }} />
              <Typography style={{ fontWeight: 600, marginTop: 12, marginBottom: 6 }}>
                Preview is not available for this document.
              </Typography>
              <Typography style={{ fontSize: "0.85rem", color: "#9aa0ad" }}>
                {previewDoc.documentName}
                {previewDoc.fileFormat ? ` · ${previewDoc.fileFormat}` : ""}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentRepository;
